import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  Building2,
  ArrowLeft,
  ExternalLink,
  Edit,
  Pause,
  Play,
  Trash2,
  Database,
  Users,
  HardDrive,
  Calendar,
  Mail,
  AlertCircle,
  Phone,
  Globe,
  MapPin,
  FileText,
  Save,
  X,
  CheckCircle,
  XCircle,
  RefreshCw,
  Briefcase,
  Puzzle,
  ToggleLeft,
  ToggleRight,
  Plus,
  FileSignature,
  DollarSign,
  Percent,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import NeonUsagePanel from '../../components/NeonUsagePanel';
import LinkedStaffPanel from '../../components/LinkedStaffPanel';

const statusColors = {
  active: 'badge-success',
  trialing: 'badge-info',
  trial: 'badge-info',
  past_due: 'badge-warning',
  canceled: 'badge-danger',
  suspended: 'badge-danger',
  pending: 'badge-gray'
};

const TenantDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [abnDetails, setAbnDetails] = useState(null);
  const [abnLoading, setAbnLoading] = useState(false);
  const [abnError, setAbnError] = useState(null);
  const [addons, setAddons] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const [togglingAddon, setTogglingAddon] = useState(null);
  const [addOnLoading, setAddOnLoading] = useState({});
  const [showAddOnModal, setShowAddOnModal] = useState(false);

  useEffect(() => {
    fetchTenant();
    fetchPlans();
    fetchAddons();
  }, [id]);

  // Fetch ABN details when tenant ABN changes
  useEffect(() => {
    if (tenant?.abn) {
      fetchAbnDetails(tenant.abn);
    }
  }, [tenant?.abn]);

  const fetchAbnDetails = async (abn) => {
    const cleanAbn = abn.replace(/\s/g, '');
    if (!/^\d{11}$/.test(cleanAbn)) {
      return;
    }

    setAbnLoading(true);
    setAbnError(null);

    try {
      const response = await axios.get(`/api/abn/details/${cleanAbn}`);
      if (response.data.success !== false) {
        setAbnDetails(response.data);
      } else {
        setAbnError(response.data.error || 'Failed to fetch ABN details');
      }
    } catch (err) {
      console.error('ABN lookup error:', err);
      setAbnError('Unable to verify ABN');
    } finally {
      setAbnLoading(false);
    }
  };

  const fetchTenant = async () => {
    try {
      const response = await adminApi.getTenant(id);
      setTenant(response.data.data);
      // Initialize edit form with tenant data
      setEditForm({
        firmName: response.data.data.name || '',
        abn: response.data.data.abn || '',
        gstRegistered: response.data.data.gstRegistered || false,
        phone: response.data.data.phone || response.data.data.billingPhone || '',
        address: response.data.data.address || '',
        city: response.data.data.city || '',
        state: response.data.data.state || '',
        postcode: response.data.data.postcode || '',
        country: response.data.data.country || 'Australia',
        website: response.data.data.website || '',
        billingContactName: response.data.data.billingContactName || response.data.data.ownerName || '',
        billingEmail: response.data.data.billingEmail || response.data.data.ownerEmail || '',
        billingPhone: response.data.data.billingPhone || '',
        billingMobile: response.data.data.billingMobile || response.data.data.ownerPhone || '',
        ownerName: response.data.data.ownerName || '',
        ownerEmail: response.data.data.ownerEmail || '',
        ownerPhone: response.data.data.ownerPhone || ''
      });
    } catch (err) {
      toast.error('Failed to load tenant');
      navigate('/tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await adminApi.getPlans();
      setPlans(response.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAddons = async () => {
    setAddonsLoading(true);
    try {
      const response = await adminApi.getTenantAddons(id);
      setAddons(response.data.data || []);
    } catch (err) {
      console.error('Failed to load add-ons:', err);
      setAddons([]);
    } finally {
      setAddonsLoading(false);
    }
  };

  const handleToggleAddOn = async (addOnSlug, addOnName, isCurrentlyEnabled) => {
    setAddOnLoading(prev => ({ ...prev, [addOnSlug]: true }));
    try {
      await adminApi.toggleTenantAddon(id, addOnSlug, !isCurrentlyEnabled);
      toast.success(isCurrentlyEnabled ? `${addOnName} disabled` : `${addOnName} enabled`);
      fetchAddons();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update add-on');
    } finally {
      setAddOnLoading(prev => ({ ...prev, [addOnSlug]: false }));
    }
  };

  const handleToggleAddon = async (addonSlug, addonName, currentlyEnabled) => {
    const action = currentlyEnabled ? 'disable' : 'enable';
    if (!confirm(`Are you sure you want to ${action} ${addonName} for ${tenant.name}?`)) return;

    setTogglingAddon(addonSlug);
    try {
      await adminApi.toggleTenantAddon(id, addonSlug, !currentlyEnabled);
      toast.success(`${addonName} ${!currentlyEnabled ? 'enabled' : 'disabled'} successfully`);
      fetchAddons();
    } catch (err) {
      toast.error(`Failed to ${action} ${addonName}`);
      console.error(err);
    } finally {
      setTogglingAddon(null);
    }
  };

  const handleSuspend = async () => {
    if (!confirm(`Are you sure you want to suspend ${tenant.name}?`)) return;

    try {
      await adminApi.suspendTenant(id, 'Suspended by admin');
      toast.success('Tenant suspended');
      fetchTenant();
    } catch (err) {
      toast.error('Failed to suspend tenant');
    }
  };

  const handleActivate = async () => {
    try {
      await adminApi.activateTenant(id);
      toast.success('Tenant activated');
      fetchTenant();
    } catch (err) {
      toast.error('Failed to activate tenant');
    }
  };

  const handleGetLoginLink = async () => {
    try {
      const response = await adminApi.getTenantLoginLink(id);
      window.open(response.data.data.loginUrl, '_blank');
    } catch (err) {
      toast.error('Failed to generate login link');
    }
  };

  const handleChangePlan = async (planId, billingCycle) => {
    try {
      await adminApi.changePlan(id, { planId, billingCycle });
      toast.success('Plan changed successfully');
      fetchTenant();
      setShowPlanModal(false);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.details || err.message || 'Failed to change plan';
      toast.error(msg);
      console.error('Change plan error:', err.response?.data || err);
    }
  };

  const handleExtendTrial = async (days) => {
    try {
      await adminApi.extendTrial(id, days);
      toast.success(`Trial extended by ${days} days`);
      fetchTenant();
      setShowTrialModal(false);
    } catch (err) {
      toast.error('Failed to extend trial');
    }
  };

  // The client portal opens the firm's database through the restricted pmp_app
  // login. PMP creates it (random password, stored encrypted); running it again rotates it.
  const [settingUpAppRole, setSettingUpAppRole] = useState(false);
  const handleSetupAppRole = async () => {
    if (!window.confirm(`Set up (or reset) the client portal's database login for ${tenant.slug}? This takes a few seconds and is safe to repeat.`)) return;
    setSettingUpAppRole(true);
    try {
      const r = await adminApi.setupAppRole(tenant.slug);
      const d = r.data || {};
      if (d.success) toast.success('Portal database access is set up');
      else toast.error('Portal database access could not be set up. Check the API worker logs.');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Portal database access could not be set up');
    } finally {
      setSettingUpAppRole(false);
    }
  };

  // Client portal uploads go to the firm's own R2 bucket (pmp-cd-<slug>) with a
  // token scoped to that bucket. The secret is encrypted before it is stored.
  const [storageForm, setStorageForm] = useState(null);
  const saveStorage = async (e) => {
    e.preventDefault();
    try {
      await adminApi.setTenantStorage({ tenant: tenant.slug, ...storageForm });
      toast.success('Client file storage is set up');
      setStorageForm(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Client file storage could not be saved');
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${tenant.name}? This action cannot be undone.`)) return;

    try {
      await adminApi.deleteTenant(id);
      toast.success('Tenant deleted');
      navigate('/tenants');
    } catch (err) {
      toast.error('Failed to delete tenant');
    }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    try {
      await adminApi.updateTenant(id, editForm);
      toast.success('Tenant details updated');
      fetchTenant();
      setShowEditModal(false);
    } catch (err) {
      toast.error('Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!tenant) return null;

  const formatBytes = (bytes) => {
    if (!bytes) return '0 GB';
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb < 0.01) {
      const mb = bytes / (1024 * 1024);
      return `${mb.toFixed(2)} MB`;
    }
    return `${gb.toFixed(2)} GB`;
  };

  // Get usage stats from API response
  const usage = tenant.usage || { users: 0, clients: 0, storage: 0 };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Link
            to="/tenants"
            className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mr-4">
              {tenant.logoUrl ? (
                <img src={tenant.logoUrl} alt={tenant.name} className="w-8 h-8 rounded" />
              ) : (
                <Building2 className="w-6 h-6 text-primary-600" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
              <p className="text-gray-500">{tenant.slug}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleGetLoginLink}
            className="btn-secondary"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Login as Tenant
          </button>
          {tenant.subscriptionStatus === 'suspended' ? (
            <button onClick={handleActivate} className="btn-primary">
              <Play className="w-4 h-4 mr-2" />
              Activate
            </button>
          ) : (
            <button onClick={handleSuspend} className="btn-secondary text-orange-600 hover:text-orange-700">
              <Pause className="w-4 h-4 mr-2" />
              Suspend
            </button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {tenant.subscriptionStatus === 'suspended' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">This tenant is suspended</p>
            <p className="text-sm text-red-700 mt-1">{tenant.suspendedReason || 'No reason provided'}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Subscription Card */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Subscription</h2>
              <span className={statusColors[tenant.subscriptionStatus] || 'badge-gray'}>
                {tenant.subscriptionStatus}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500">Current Plan</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {tenant.subscriptionPlan?.name || 'No plan'}
                </p>
                {tenant.subscriptionPlan?.minUsers > 1 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {tenant.subscriptionPlan.minUsers} users included
                    {parseFloat(tenant.subscriptionPlan.pricePerUserMonthly) > 0 && (
                      <span> + ${tenant.subscriptionPlan.pricePerUserMonthly}/user after</span>
                    )}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Billing Cycle</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {tenant.billingCycle === 'yearly' ? 'Annual' : 'Monthly'}
                </p>
              </div>
              {tenant.subscriptionStatus === 'trialing' && tenant.trialEndsAt && (
                <div>
                  <p className="text-sm text-gray-500">Trial Ends</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {new Date(tenant.trialEndsAt).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    {tenant.daysUntilTrialEnd} days remaining
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Current Period End</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {tenant.currentPeriodEnd
                    ? new Date(tenant.currentPeriodEnd).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowPlanModal(true)}
                className="btn-secondary"
              >
                Change Plan
              </button>
              {(tenant.subscriptionStatus === 'trialing' || tenant.subscriptionStatus === 'trial') && (
                <button
                  onClick={() => setShowTrialModal(true)}
                  className="btn-secondary"
                >
                  Extend Trial
                </button>
              )}
            </div>
          </div>

          {/* Add-ons Card */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <Puzzle className="w-5 h-5 text-primary-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Add-ons & Modules</h2>
              </div>
              <button
                onClick={() => setShowAddOnModal(true)}
                className="btn-secondary text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Manage
              </button>
            </div>

            {addons.filter(a => a.enabled).length > 0 ? (
              <div className="space-y-3">
                {addons.filter(a => a.enabled).map(addon => (
                  <div key={addon.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                        <Puzzle className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{addon.name}</p>
                        <p className="text-xs text-gray-500">
                          ${addon.priceMonthly}/mo
                          {parseFloat(addon.transactionFeePercent) > 0 && (
                            <span className="text-orange-600 font-medium"> + {addon.transactionFeePercent}% fee</span>
                          )}
                          {addon.category && (
                            <span className="ml-2 text-xs text-gray-400">
                              {addon.category === 'integration' ? 'Integration' : addon.category === 'module' ? 'Module' : 'Feature'}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <span className="badge-success text-xs">Active</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Puzzle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No add-ons enabled</p>
                <button
                  onClick={() => setShowAddOnModal(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 mt-2"
                >
                  Enable add-ons
                </button>
              </div>
            )}
          </div>

          {/* Usage Stats */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Usage</h2>
            <div className="grid grid-cols-3 gap-6">
              <Link
                to={`/marketing/contacts?tenantId=${tenant.id}`}
                className="text-center group cursor-pointer hover:bg-blue-50 rounded-xl p-3 -m-3 transition-colors"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-blue-200 transition-colors">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900 group-hover:text-blue-600">{usage.users}</p>
                <p className="text-sm text-gray-500 group-hover:text-blue-500">Users</p>
              </Link>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Building2 className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{usage.clients}</p>
                <p className="text-sm text-gray-500">Clients</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <HardDrive className="w-6 h-6 text-purple-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatBytes(usage.storage)}</p>
                <p className="text-sm text-gray-500">Storage</p>
              </div>
            </div>
          </div>

          {/* Neon Database Usage */}
          <div className="card p-6">
            <NeonUsagePanel tenantId={tenant.id} />
          </div>

          {/* Linked staff from other practices (multi-firm access) */}
          <div className="card p-6">
            <LinkedStaffPanel tenantId={tenant.id} />
          </div>

          {/* Billing & Pricing Overrides */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Billing Overrides</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">Set custom pricing for this tenant. Overrides apply to invoices instead of standard plan rates.</p>

            <div className="space-y-4">
              {/* Free Account Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border-2 border-gray-200 bg-white">
                <div>
                  <h3 className="font-semibold text-gray-900">Free Account</h3>
                  <p className="text-sm text-gray-500">Waive all subscription and add-on charges for this tenant</p>
                </div>
                <button
                  onClick={async () => {
                    const isFree = tenant.features?.subscription_free === true;
                    try {
                      await adminApi.updateTenantFeatures(id, {
                        subscription_free: !isFree,
                        subscription_free_updated_at: new Date().toISOString()
                      });
                      toast.success(isFree ? 'Free account disabled' : 'Account marked as free');
                      fetchTenant();
                    } catch (err) {
                      const msg = err.response?.data?.error || err.message || 'Failed to update';
                      toast.error(msg);
                      console.error('Free account toggle error:', err.response?.data || err);
                    }
                  }}
                  className="flex-shrink-0"
                >
                  {tenant.features?.subscription_free ? (
                    <ToggleRight className="w-10 h-6 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-10 h-6 text-gray-300" />
                  )}
                </button>
              </div>

              {/* Custom Subscription Price */}
              {!tenant.features?.subscription_free && (
                <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Custom Subscription Price</h4>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Monthly ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={tenant.subscriptionPlan?.priceMonthly || 'Standard'}
                        defaultValue={tenant.features?.subscription_custom_price_monthly ?? ''}
                        onBlur={async (e) => {
                          const val = e.target.value;
                          try {
                            await adminApi.updateTenantFeatures(id, {
                              subscription_custom_price_monthly: val === '' ? null : parseFloat(val)
                            });
                            toast.success('Custom price saved');
                            fetchTenant();
                          } catch (err) {
                            toast.error('Failed to save');
                          }
                        }}
                        className="input text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Yearly ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={tenant.subscriptionPlan?.priceYearly || 'Standard'}
                        defaultValue={tenant.features?.subscription_custom_price_yearly ?? ''}
                        onBlur={async (e) => {
                          const val = e.target.value;
                          try {
                            await adminApi.updateTenantFeatures(id, {
                              subscription_custom_price_yearly: val === '' ? null : parseFloat(val)
                            });
                            toast.success('Custom price saved');
                            fetchTenant();
                          } catch (err) {
                            toast.error('Failed to save');
                          }
                        }}
                        className="input text-sm"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Leave blank to use standard plan pricing. Enter 0 for free.</p>
                </div>
              )}

              {/* Pricing Note */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Pricing Note</label>
                <input
                  type="text"
                  placeholder="e.g. Partner agreement, 6-month free trial, etc."
                  defaultValue={tenant.features?.pricing_note ?? ''}
                  onBlur={async (e) => {
                    try {
                      await adminApi.updateTenantFeatures(id, { pricing_note: e.target.value || null });
                      fetchTenant();
                    } catch (err) {
                      toast.error('Failed to save note');
                    }
                  }}
                  className="input text-sm"
                />
              </div>
            </div>
          </div>

          {/* Add-ons & Modules */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Add-ons & Modules</h2>
            </div>

            {addonsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
            ) : addons.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No add-ons available</p>
            ) : (
              <div className="space-y-4">
                {addons.map((addon) => {
                  const customPriceKey = `${addon.slug}_custom_price`;
                  const customPrice = tenant.features?.[customPriceKey];
                  const isFreeAccount = tenant.features?.subscription_free === true;

                  return (
                    <div
                      key={addon.id}
                      className={`rounded-lg border-2 p-4 transition-colors ${
                        addon.enabled
                          ? 'border-green-200 bg-green-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            addon.enabled ? 'bg-green-100' : 'bg-gray-100'
                          }`}>
                            <FileSignature className={`w-5 h-5 ${
                              addon.enabled ? 'text-green-600' : 'text-gray-400'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-gray-900">{addon.name}</h3>
                              {addon.enabled ? (
                                <span className="badge-success text-xs">Active</span>
                              ) : (
                                <span className="badge-gray text-xs">Inactive</span>
                              )}
                              {addon.enabled && (isFreeAccount || customPrice === 0) && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Free</span>
                              )}
                              {addon.enabled && customPrice > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Custom: ${customPrice}/mo</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{addon.description}</p>

                            {/* Standard Pricing */}
                            <div className="flex items-center gap-4 mt-3">
                              <div className="flex items-center gap-1 text-sm">
                                <DollarSign className="w-4 h-4 text-green-600" />
                                <span className={`font-semibold ${(isFreeAccount || customPrice !== undefined) ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                  ${addon.priceMonthly}
                                </span>
                                <span className="text-gray-500">/month</span>
                              </div>
                              {parseFloat(addon.transactionFeePercent) > 0 && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Percent className="w-4 h-4 text-orange-500" />
                                  <span className="font-semibold text-gray-900">{addon.transactionFeePercent}%</span>
                                  <span className="text-gray-500">transaction fee</span>
                                </div>
                              )}
                            </div>

                            {/* Custom Price Input (shown when enabled) */}
                            {addon.enabled && !isFreeAccount && (
                              <div className="mt-3 flex items-center gap-2">
                                <label className="text-xs text-gray-500 whitespace-nowrap">Custom price:</label>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-gray-400">$</span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="Standard"
                                    defaultValue={customPrice ?? ''}
                                    onBlur={async (e) => {
                                      const val = e.target.value;
                                      try {
                                        await adminApi.updateTenantFeatures(id, {
                                          [customPriceKey]: val === '' ? null : parseFloat(val)
                                        });
                                        toast.success(`Custom price ${val === '' ? 'removed' : 'saved'} for ${addon.name}`);
                                        fetchTenant();
                                      } catch (err) {
                                        toast.error('Failed to save custom price');
                                      }
                                    }}
                                    className="input text-sm w-24"
                                  />
                                  <span className="text-xs text-gray-400">/mo</span>
                                </div>
                                <span className="text-xs text-gray-400">(0 = free, blank = standard)</span>
                              </div>
                            )}

                            {addon.enabled && addon.enabledAt && (
                              <p className="text-xs text-gray-400 mt-2">
                                Enabled {new Date(addon.enabledAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={() => handleToggleAddon(addon.slug, addon.name, addon.enabled)}
                          disabled={togglingAddon === addon.slug}
                          className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                            addon.enabled
                              ? 'border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50'
                              : 'bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50'
                          }`}
                        >
                          {togglingAddon === addon.slug ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {addon.enabled ? 'Disabling...' : 'Enabling...'}
                            </>
                          ) : addon.enabled ? (
                            'Disable'
                          ) : (
                            'Enable'
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent Invoices */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent Invoices</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {tenant.invoices?.length > 0 ? (
                tenant.invoices.map((invoice) => (
                  <div key={invoice.id} className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {invoice.invoiceNumber || `INV-${invoice.id.slice(0, 8)}`}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(invoice.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        ${parseFloat(invoice.total).toFixed(2)} {invoice.currency}
                      </p>
                      <span className={`badge ${invoice.status === 'paid' ? 'badge-success' : 'badge-warning'}`}>
                        {invoice.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-8 text-center text-gray-500">
                  No invoices yet
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Business Details Card */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Details</h2>
            <dl className="space-y-4">
              {/* Business Name */}
              <div>
                <dt className="text-sm text-gray-500">Business Name</dt>
                <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                  <Briefcase className="w-4 h-4 mr-2 text-gray-400" />
                  {tenant.name || tenant.displayName || 'Not set'}
                </dd>
              </div>

              {/* ABN with Status */}
              <div>
                <dt className="text-sm text-gray-500 flex items-center justify-between">
                  <span>ABN</span>
                  {tenant.abn && (
                    <button
                      onClick={() => fetchAbnDetails(tenant.abn)}
                      disabled={abnLoading}
                      className="text-xs text-primary-600 hover:text-primary-700 flex items-center"
                    >
                      <RefreshCw className={`w-3 h-3 mr-1 ${abnLoading ? 'animate-spin' : ''}`} />
                      Verify
                    </button>
                  )}
                </dt>
                <dd className="text-sm font-medium text-gray-900 mt-1">
                  {tenant.abn ? (
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 mr-2 text-gray-400" />
                        <span className="font-mono">{tenant.abn}</span>
                      </div>

                      {/* ABN Status from lookup */}
                      {abnLoading ? (
                        <div className="text-xs text-gray-500 flex items-center ml-6">
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          Verifying ABN...
                        </div>
                      ) : abnDetails ? (
                        <div className="ml-6 space-y-1">
                          <div className="flex items-center">
                            {abnDetails.abnStatus === 'Active' ? (
                              <CheckCircle className="w-4 h-4 mr-1 text-green-500" />
                            ) : (
                              <XCircle className="w-4 h-4 mr-1 text-red-500" />
                            )}
                            <span className={`text-xs font-medium ${abnDetails.abnStatus === 'Active' ? 'text-green-700' : 'text-red-700'}`}>
                              {abnDetails.abnStatus || 'Unknown'}
                            </span>
                          </div>
                          {abnDetails.entityName && (
                            <p className="text-xs text-gray-600">{abnDetails.entityName}</p>
                          )}
                          {abnDetails.entityType && (
                            <p className="text-xs text-gray-500">{abnDetails.entityType}</p>
                          )}
                        </div>
                      ) : abnError ? (
                        <div className="text-xs text-orange-600 ml-6">{abnError}</div>
                      ) : null}
                    </div>
                  ) : (
                    <span className="text-gray-400">Not set</span>
                  )}
                </dd>
              </div>

              {/* GST Registration Status */}
              <div>
                <dt className="text-sm text-gray-500">GST Registration</dt>
                <dd className="text-sm font-medium mt-1">
                  {abnDetails?.gstStatus ? (
                    <div className="flex items-center">
                      {abnDetails.gstStatus === 'Registered' || abnDetails.gstRegistered ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                          <span className="text-green-700">Registered</span>
                          {abnDetails.gstEffectiveFrom && (
                            <span className="text-xs text-gray-500 ml-2">
                              (since {new Date(abnDetails.gstEffectiveFrom).toLocaleDateString()})
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="text-gray-600">Not Registered</span>
                        </>
                      )}
                    </div>
                  ) : tenant.gstRegistered ? (
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      <span className="text-green-700">Registered</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <XCircle className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-gray-600">Not Registered</span>
                    </div>
                  )}
                </dd>
              </div>

              {/* Contact Number */}
              <div>
                <dt className="text-sm text-gray-500">Contact Number</dt>
                <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-gray-400" />
                  {tenant.billingPhone || tenant.billingMobile || tenant.ownerPhone || 'Not set'}
                </dd>
              </div>

              {/* Address */}
              <div>
                <dt className="text-sm text-gray-500">Address</dt>
                <dd className="text-sm font-medium text-gray-900 mt-1 flex items-start">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                  {(tenant.address || tenant.city || tenant.state || abnDetails?.state) ? (
                    <span>
                      {tenant.address && <>{tenant.address}<br /></>}
                      {tenant.city && `${tenant.city}, `}
                      {(tenant.state || abnDetails?.state) && `${tenant.state || abnDetails?.state} `}
                      {tenant.postcode || abnDetails?.postcode || ''}
                      {tenant.country && tenant.country !== 'Australia' && <><br />{tenant.country}</>}
                    </span>
                  ) : (
                    <span className="text-gray-400">Not set</span>
                  )}
                </dd>
              </div>

              {/* Website */}
              <div>
                <dt className="text-sm text-gray-500">Website</dt>
                <dd className="text-sm font-medium mt-1 flex items-center">
                  <Globe className="w-4 h-4 mr-2 text-gray-400" />
                  {tenant.website ? (
                    <a href={tenant.website.startsWith('http') ? tenant.website : `https://${tenant.website}`}
                       target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                      {tenant.website}
                    </a>
                  ) : (
                    <span className="text-gray-400">Not set</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Portal Details Card */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Portal Details</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-gray-500">Domain</dt>
                <dd className="text-sm font-medium text-primary-600 mt-1">
                  <a href={tenant.loginUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    {tenant.loginUrl}
                  </a>
                </dd>
              </div>

              {/* Billing Contact */}
              <div className="pt-4 border-t border-gray-200">
                <dt className="text-sm font-medium text-gray-700 mb-2">Billing Contact</dt>
                {(tenant.billingContactName || tenant.ownerName) && (
                  <dd className="text-sm text-gray-900 mt-1">
                    {tenant.billingContactName || tenant.ownerName}
                  </dd>
                )}
                <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-gray-400" />
                  {tenant.billingEmail || tenant.ownerEmail || 'Not set'}
                </dd>
                {(tenant.billingMobile || tenant.ownerPhone) && (
                  <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    {tenant.billingMobile || tenant.ownerPhone}
                  </dd>
                )}
                {tenant.billingPhone && tenant.billingPhone !== tenant.billingMobile && (
                  <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    {tenant.billingPhone} (Office)
                  </dd>
                )}
              </div>

              <div>
                <dt className="text-sm text-gray-500">Created</dt>
                <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                  {new Date(tenant.createdAt).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>

          {/* Actions Card */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
            <div className="space-y-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="w-full btn-secondary justify-start"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Details
              </button>
              <button
                onClick={handleSetupAppRole}
                disabled={settingUpAppRole}
                className="w-full btn-secondary justify-start"
              >
                <Database className="w-4 h-4 mr-2" />
                {settingUpAppRole ? 'Setting up…' : 'Set up portal database access'}
              </button>
              <button
                onClick={() => setStorageForm(storageForm ? null : { accountId: '', bucket: `pmp-cd-${tenant.slug}`, accessKeyId: '', secretAccessKey: '' })}
                className="w-full btn-secondary justify-start"
              >
                <Database className="w-4 h-4 mr-2" />
                Set up client file storage
              </button>
              {storageForm && (
                <form onSubmit={saveStorage} className="space-y-2 rounded-lg border p-3 text-sm">
                  <p className="text-xs text-gray-500">Cloudflare → R2: create the bucket, then Manage R2 API tokens → Object Read &amp; Write, applied to that bucket only. Paste the keys here; the secret is stored encrypted and never shown again.</p>
                  {[['accountId', 'Cloudflare account ID'], ['bucket', 'Bucket name'], ['accessKeyId', 'Access key ID'], ['secretAccessKey', 'Secret access key']].map(([k, label]) => (
                    <label key={k} className="block">
                      <span className="text-xs text-gray-600">{label}</span>
                      <input type={k === 'secretAccessKey' ? 'password' : 'text'} autoComplete="off" required className="mt-1 w-full rounded border px-2 py-1"
                        value={storageForm[k]} onChange={(e) => setStorageForm({ ...storageForm, [k]: e.target.value })} />
                    </label>
                  ))}
                  <button type="submit" className="btn-primary w-full">Save storage</button>
                </form>
              )}
              <button
                onClick={handleDelete}
                className="w-full btn-secondary justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Tenant
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Details Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-900">Edit Tenant Details</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Business Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Business Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Business Name</label>
                    <input
                      type="text"
                      value={editForm.firmName}
                      onChange={(e) => setEditForm({...editForm, firmName: e.target.value})}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">ABN</label>
                    <input
                      type="text"
                      value={editForm.abn}
                      onChange={(e) => setEditForm({...editForm, abn: e.target.value})}
                      className="input"
                      placeholder="12 345 678 901"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.gstRegistered}
                        onChange={(e) => setEditForm({...editForm, gstRegistered: e.target.checked})}
                        className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">GST Registered</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Contact Number</label>
                    <input
                      type="tel"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                      className="input"
                      placeholder="(02) 1234 5678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Website</label>
                    <input
                      type="url"
                      value={editForm.website}
                      onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                      className="input"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Address</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Street Address</label>
                    <input
                      type="text"
                      value={editForm.address}
                      onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">City</label>
                    <input
                      type="text"
                      value={editForm.city}
                      onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">State</label>
                    <select
                      value={editForm.state}
                      onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                      className="input"
                    >
                      <option value="">Select state</option>
                      <option value="NSW">New South Wales</option>
                      <option value="VIC">Victoria</option>
                      <option value="QLD">Queensland</option>
                      <option value="WA">Western Australia</option>
                      <option value="SA">South Australia</option>
                      <option value="TAS">Tasmania</option>
                      <option value="ACT">Australian Capital Territory</option>
                      <option value="NT">Northern Territory</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Postcode</label>
                    <input
                      type="text"
                      value={editForm.postcode}
                      onChange={(e) => setEditForm({...editForm, postcode: e.target.value})}
                      className="input"
                      maxLength={4}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Country</label>
                    <input
                      type="text"
                      value={editForm.country}
                      onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                      className="input"
                    />
                  </div>
                </div>
              </div>

              {/* Billing Contact */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Billing Contact</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Contact Name</label>
                    <input
                      type="text"
                      value={editForm.billingContactName}
                      onChange={(e) => setEditForm({...editForm, billingContactName: e.target.value})}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Email</label>
                    <input
                      type="email"
                      value={editForm.billingEmail}
                      onChange={(e) => setEditForm({...editForm, billingEmail: e.target.value})}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Mobile (for SMS)</label>
                    <input
                      type="tel"
                      value={editForm.billingMobile}
                      onChange={(e) => setEditForm({...editForm, billingMobile: e.target.value})}
                      className="input"
                      placeholder="04XX XXX XXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={editForm.billingPhone}
                      onChange={(e) => setEditForm({...editForm, billingPhone: e.target.value})}
                      className="input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={() => setShowEditModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleSaveDetails} disabled={saving} className="btn-primary">
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Plan</h3>
            <div className="space-y-3">
              {plans.length > 0 ? plans.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => handleChangePlan(plan.id, tenant.billingCycle)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                    tenant.subscriptionPlanId === plan.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-primary-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{plan.name}</p>
                      <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                    </div>
                    <p className="font-semibold text-gray-900">
                      ${tenant.billingCycle === 'yearly' ? plan.priceYearly : plan.priceMonthly}
                      <span className="text-xs text-gray-500">
                        /{tenant.billingCycle === 'yearly' ? 'yr' : 'mo'}
                      </span>
                    </p>
                  </div>
                </button>
              )) : (
                <p className="text-gray-500 text-center py-4">No plans available</p>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowPlanModal(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Trial Modal */}
      {showTrialModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Extend Trial</h3>
            <p className="text-sm text-gray-500 mb-4">
              Select how many days to extend the trial period.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[7, 14, 30].map((days) => (
                <button
                  key={days}
                  onClick={() => handleExtendTrial(days)}
                  className="btn-secondary"
                >
                  {days} days
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button onClick={() => setShowTrialModal(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Add-ons Modal */}
      {showAddOnModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Manage Add-ons</h3>
                <p className="text-sm text-gray-500 mt-1">Toggle add-ons for {tenant.name}</p>
              </div>
              <button onClick={() => setShowAddOnModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-3">
              {addons.map(addOn => {
                const isEnabled = addOn.enabled;
                const isLoading = addOnLoading[addOn.slug];

                return (
                  <div
                    key={addOn.id}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      isEnabled
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 mr-4">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">{addOn.name}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            addOn.category === 'integration' ? 'bg-blue-100 text-blue-700' :
                            addOn.category === 'module' ? 'bg-purple-100 text-purple-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {addOn.category === 'integration' ? 'Integration' : addOn.category === 'module' ? 'Module' : 'Feature'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{addOn.description}</p>
                        <p className="text-sm font-medium text-gray-700 mt-2">
                          ${addOn.priceMonthly}/month
                          <span className="text-gray-400 font-normal"> ({`$${addOn.priceYearly}/year`})</span>
                        </p>
                        {parseFloat(addOn.transactionFeePercent) > 0 && (
                          <p className="text-xs font-medium text-orange-600 mt-1">
                            + {addOn.transactionFeePercent}% transaction fee
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleToggleAddOn(addOn.slug, addOn.name, isEnabled)}
                        disabled={isLoading}
                        className={`flex-shrink-0 p-1 rounded-lg transition-colors ${
                          isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                      >
                        {isLoading ? (
                          <div className="w-10 h-6 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                          </div>
                        ) : isEnabled ? (
                          <ToggleRight className="w-10 h-6 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-10 h-6 text-gray-300" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}

              {addons.length === 0 && (
                <p className="text-center text-gray-500 py-4">No add-ons available</p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end sticky bottom-0 bg-white">
              <button onClick={() => setShowAddOnModal(false)} className="btn-primary">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantDetailPage;
